import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { X, Plus, Tag } from 'lucide-react';
import { theme } from '../../styles/theme';
import { Button } from './Button';
import { useLabels } from '../../context/LabelContext';
import toast from 'react-hot-toast';

const LabelSelectorContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.md};
`;

const LabelSelectorLabel = styled.label`
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
  color: ${({ theme }) => theme.colors.text};
  margin-bottom: ${({ theme }) => theme.spacing.xs};
`;

const SelectedLabelsContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.spacing.sm};
  margin-bottom: ${({ theme }) => theme.spacing.md};
`;

const SelectedLabel = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.xs};
  padding: ${({ theme }) => theme.spacing.xs} ${({ theme }) => theme.spacing.sm};
  background-color: ${({ $color }) => $color || theme.colors.primary};
  color: white;
  border-radius: ${({ theme }) => theme.borderRadius.md};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
`;

const RemoveLabelButton = styled.button`
  background: none;
  border: none;
  color: white;
  cursor: pointer;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  width: 16px;
  height: 16px;
  
  &:hover {
    background-color: rgba(255, 255, 255, 0.2);
  }
`;

const DropdownContainer = styled.div`
  position: relative;
`;

const DropdownButton = styled(Button)`
  width: 100%;
  justify-content: space-between;
  background-color: ${({ theme }) => theme.colors.surface};
  border: 1px solid ${({ theme }) => theme.colors.gray300};
  color: ${({ theme }) => theme.colors.text};
  
  &:hover {
    background-color: ${({ theme }) => theme.colors.surfaceHover};
  }
`;

const Dropdown = styled.div`
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  background-color: ${({ theme }) => theme.colors.surface};
  border: 1px solid ${({ theme }) => theme.colors.gray300};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  box-shadow: ${({ theme }) => theme.shadows.lg};
  z-index: 1000;
  max-height: 200px;
  overflow-y: auto;
  margin-top: ${({ theme }) => theme.spacing.xs};
`;

const DropdownItem = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.sm};
  padding: ${({ theme }) => theme.spacing.sm};
  cursor: pointer;
  border-bottom: 1px solid ${({ theme }) => theme.colors.gray200};
  
  &:last-child {
    border-bottom: none;
  }
  
  &:hover {
    background-color: ${({ theme }) => theme.colors.surfaceHover};
  }
`;

const LabelColor = styled.div`
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background-color: ${({ $color }) => $color || theme.colors.primary};
`;

const LabelName = styled.span`
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  color: ${({ theme }) => theme.colors.text};
`;

const CreateLabelButton = styled(Button)`
  width: 100%;
  margin-top: ${({ theme }) => theme.spacing.sm};
  background-color: ${({ theme }) => theme.colors.gray100};
  color: ${({ theme }) => theme.colors.text};
  border: 1px dashed ${({ theme }) => theme.colors.gray300};
  
  &:hover {
    background-color: ${({ theme }) => theme.colors.gray200};
  }
`;

const CreateLabelForm = styled.div`
  padding: ${({ theme }) => theme.spacing.md};
  border-top: 1px solid ${({ theme }) => theme.colors.gray200};
`;

const CreateLabelInput = styled.input`
  width: 100%;
  padding: ${({ theme }) => theme.spacing.sm};
  border: 1px solid ${({ theme }) => theme.colors.gray300};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  margin-bottom: ${({ theme }) => theme.spacing.sm};
  
  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary};
  }
`;

const ColorPicker = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing.xs};
  margin-bottom: ${({ theme }) => theme.spacing.sm};
`;

const ColorOption = styled.button`
  width: 24px;
  height: 24px;
  border-radius: 50%;
  border: 2px solid ${({ $selected, theme }) => $selected ? theme.colors.text : 'transparent'};
  background-color: ${({ $color }) => $color};
  cursor: pointer;
  
  &:hover {
    transform: scale(1.1);
  }
`;

const FormButtons = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing.sm};
`;

const availableColors = [
  '#3B82F6', '#EF4444', '#10B981', '#F59E0B',
  '#8B5CF6', '#EC4899', '#06B6D4', '#84CC16',
  '#F97316', '#6B7280', '#8B5CF6', '#F59E0B'
];

function LabelSelector({ selectedLabelIds = [], onLabelsChange, disabled = false }) {
  const [isOpen, setIsOpen] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [newLabelName, setNewLabelName] = useState('');
  const [newLabelColor, setNewLabelColor] = useState(availableColors[0]);
  
  const { labels, loading, error, createLabel } = useLabels();
  
  const selectedLabels = labels?.filter(label => selectedLabelIds.includes(label.id)) || [];
  const availableLabels = labels?.filter(label => !selectedLabelIds.includes(label.id)) || [];

  const handleLabelToggle = (labelId) => {
    // Validate labelId before processing
    if (!labelId || labelId <= 0) {
      console.warn('Invalid labelId in handleLabelToggle:', labelId);
      return;
    }
    
    const newSelectedIds = selectedLabelIds.includes(labelId)
      ? selectedLabelIds.filter(id => id !== labelId)
      : [...selectedLabelIds, labelId];
    
    onLabelsChange(newSelectedIds);
  };

  const handleRemoveLabel = (labelId) => {
    const newSelectedIds = selectedLabelIds.filter(id => id !== labelId);
    onLabelsChange(newSelectedIds);
  };

  const handleCreateLabel = async () => {
    if (!newLabelName.trim()) return;

    try {
      const newLabel = await createLabel({
        name: newLabelName.trim(),
        color: newLabelColor
      });
      
      // Validate the new label ID
      if (!newLabel.id || newLabel.id <= 0) {
        console.warn('Invalid label ID returned from createLabel:', newLabel.id);
        return;
      }
      
      // Add the new label to selected labels
      const newSelectedIds = [...selectedLabelIds, newLabel.id];
      onLabelsChange(newSelectedIds);
      
      // Reset form
      setNewLabelName('');
      setNewLabelColor(availableColors[0]);
      setIsCreating(false);
    } catch (error) {
      // Error is already handled in the context
    }
  };

  if (loading) {
    return <div>Loading labels...</div>;
  }

  if (error) {
    return <div>Error loading labels</div>;
  }

  return (
    <LabelSelectorContainer>
      <LabelSelectorLabel>Labels</LabelSelectorLabel>
      
      {selectedLabels.length > 0 && (
        <SelectedLabelsContainer>
          {selectedLabels.map(label => (
            <SelectedLabel key={label.id} $color={label.color}>
              <Tag size={12} />
              {label.name}
              <RemoveLabelButton
                type="button"
                onClick={() => handleRemoveLabel(label.id)}
                disabled={disabled}
              >
                <X size={12} />
              </RemoveLabelButton>
            </SelectedLabel>
          ))}
        </SelectedLabelsContainer>
      )}

      <DropdownContainer>
        <DropdownButton
          onClick={() => setIsOpen(!isOpen)}
          disabled={disabled}
          variant="outline"
        >
          <span>Add Labels</span>
          <Tag size={16} />
        </DropdownButton>

        {isOpen && (
          <Dropdown>
            {availableLabels.map(label => (
              <DropdownItem
                key={label.id}
                onClick={() => handleLabelToggle(label.id)}
              >
                <LabelColor $color={label.color} />
                <LabelName>{label.name}</LabelName>
              </DropdownItem>
            ))}
            
            {!isCreating ? (
              <CreateLabelButton
                type="button"
                onClick={() => setIsCreating(true)}
                variant="outline"
              >
                <Plus size={16} />
                Create New Label
              </CreateLabelButton>
            ) : (
              <CreateLabelForm>
                <CreateLabelInput
                  type="text"
                  placeholder="Label name"
                  value={newLabelName}
                  onChange={(e) => setNewLabelName(e.target.value)}
                  autoFocus
                />
                
                <ColorPicker>
                  {availableColors.map(color => (
                    <ColorOption
                      key={color}
                      type="button"
                      $color={color}
                      $selected={newLabelColor === color}
                      onClick={() => setNewLabelColor(color)}
                    />
                  ))}
                </ColorPicker>
                
                <FormButtons>
                  <Button
                    type="button"
                    onClick={handleCreateLabel}
                    disabled={!newLabelName.trim()}
                    size="sm"
                  >
                    Create
                  </Button>
                  <Button
                    type="button"
                    onClick={() => {
                      setIsCreating(false);
                      setNewLabelName('');
                    }}
                    variant="outline"
                    size="sm"
                  >
                    Cancel
                  </Button>
                </FormButtons>
              </CreateLabelForm>
            )}
          </Dropdown>
        )}
      </DropdownContainer>
    </LabelSelectorContainer>
  );
}

export default LabelSelector;
